/**
 * Pantry vs grocery-line matching aligned with backend/services/recipe_search_filters.py
 * (_words_for_match, _singular_for_match, _pantry_matches_recipe_ingredient).
 * Recipe-style list lines (e.g. "1 medium onion, diced") must match pantry names ("onions")
 * via token overlap, not exact string equality. When the pantry name appears as a contiguous
 * phrase in the line, matching uses word boundaries so short names (e.g. "sage") do not match
 * inside longer words ("sausage").
 */

/**
 * Strip combining marks so jalapeño and jalapeno match (mirrors backend _fold_accents_for_match).
 *
 * @param {string} s
 * @returns {string}
 */
export function foldAccentsForMatch(s) {
  if (!s) return s
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** @type {Record<string, string>} */
const IRREGULAR_SINGULARS = {
  chillies: 'chilli',
  chilies: 'chili',
  chiles: 'chile',
  chilis: 'chili',
}

const CHILE_CANONICAL = 'chili'
const CHILE_SPELLING_VARIANTS = new Set([
  'chili', 'chilis', 'chilies',
  'chilli', 'chillies',
  'chile', 'chiles',
  'chilly', 'chily',
])
const CHILE_PEPPER_WORDS = new Set([
  ...CHILE_SPELLING_VARIANTS,
  'jalapeno', 'jalapenos', 'serrano', 'habanero',
  'poblano', 'anaheim', 'thai', 'chipotle', 'cayenne', 'guajillo', 'pasilla', 'bird', 'birds',
])
const CHILE_SPECIFICITY_WORDS = new Set([
  'thai', 'bird', 'birds', 'eye', 'serrano', 'jalapeno', 'habanero', 'poblano', 'anaheim',
  'guajillo', 'pasilla', 'chipotle',
])
const CHILE_FLAKE_WORDS = new Set(['flake', 'flakes'])

/** Common grocery misspellings → the token pantry matching already uses. */
const TOKEN_SPELLING_ALIASES = {
  jalepeno: 'jalapeno',
  jalepenos: 'jalapeno',
}

/** Named peppers: "chile" / "pepper" is the species, not a second ingredient. */
const NAMED_FRESH_PEPPER_HEAD_WORDS = new Set([
  'jalapeno', 'serrano', 'habanero', 'poblano', 'anaheim',
])
const GENERIC_PEPPER_POD_WORDS = new Set([
  'chili', 'chile', 'chiles', 'chilli', 'chillies', 'chilis', 'chilies',
  'pepper', 'peppers',
])

/**
 * @param {string} word
 * @returns {string}
 */
function aliasSpellingToken(word) {
  return TOKEN_SPELLING_ALIASES[word] || word
}

/**
 * @param {string} word
 * @returns {string}
 */
export function singularForMatch(word) {
  if (word.length <= 2) return word
  if (IRREGULAR_SINGULARS[word]) return IRREGULAR_SINGULARS[word]
  if (word.endsWith('ies') && word.length > 3) {
    return `${word.slice(0, -3)}y`
  }
  if (word.endsWith('es') && word.length > 3) {
    const stem = word.slice(0, -2)
    if (/(x|z|ch|sh|ss|o)$/.test(stem)) {
      return stem
    }
    return word.slice(0, -1)
  }
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 2) {
    return word.slice(0, -1)
  }
  return word
}

/**
 * @param {string} word
 * @returns {string | null}
 */
function chileCanonicalToken(word) {
  if (!word) return null
  if (CHILE_SPELLING_VARIANTS.has(word)) return CHILE_CANONICAL
  const singular = singularForMatch(word)
  if (CHILE_SPELLING_VARIANTS.has(singular)) return CHILE_CANONICAL
  return null
}

/**
 * @param {string} s
 * @returns {Set<string>}
 */
export function wordsForMatch(s) {
  if (!s || typeof s !== 'string') {
    return new Set()
  }
  const tokens = foldAccentsForMatch(s.toLowerCase()).match(/[a-z0-9]+/g) || []
  /** @type {Set<string>} */
  const out = new Set()
  for (const raw of tokens) {
    if (raw.length >= 2 && !/^\d+$/.test(raw)) {
      const t = aliasSpellingToken(raw)
      out.add(t)
      const singular = singularForMatch(t)
      if (singular !== t) {
        out.add(aliasSpellingToken(singular))
      }
      const chile = chileCanonicalToken(t)
      if (chile) out.add(chile)
    }
  }
  return out
}

const PREP_AND_QUALITY_MODIFIERS = new Set([
  'ground', 'fresh', 'dried', 'frozen', 'canned', 'chopped', 'sliced', 'diced',
  'minced', 'crushed', 'whole', 'raw', 'cooked', 'large', 'small', 'medium',
  'organic', 'wild', 'caught', 'lean', 'boneless', 'skinless', 'peeled', 'deveined',
  'angus', 'prime', 'trimmed', 'extra', 'virgin', 'unsalted', 'salted',
])

const OPTIONAL_CUT_FORM_WORDS = new Set(['fillet', 'fillets', 'filet'])

/** Product-form words that mean a different item than the head noun alone. */
const DISTINCT_PRODUCT_FORM_WORDS = new Set([
  'leaf', 'leaves', 'peel', 'zest', 'juice', 'oil', 'extract', 'powder', 'powders',
  'paste', 'sauce', 'yogurt', 'seeds', 'seed', 'nuts', 'nut', 'chips', 'chip', 'wine',
  'vinegar', 'syrup', 'nectar', 'water', 'puree', 'concentrate',
])

/** Named condiments where paste/sauce is the usual jar label, not a different product. */
const CONDIMENT_OPTIONAL_FORM_WORDS = {
  harissa: new Set(['paste']),
  tzatziki: new Set(['sauce']),
}

const MEAT_CUT_WORDS = new Set([
  'breast', 'thigh', 'wing', 'wings', 'drumstick', 'drumsticks', 'tenderloin',
  'loin', 'chop', 'chops', 'cutlet', 'cutlets', 'fillet', 'fillets', 'filet',
  'leg', 'legs', 'rack', 'shank', 'shoulder', 'brisket',
])

const HERB_HEAD_WORDS = new Set([
  'parsley', 'cilantro', 'basil', 'dill', 'mint', 'thyme', 'oregano', 'rosemary',
  'sage', 'chive', 'chives', 'tarragon', 'marjoram', 'coriander', 'bay',
])

const HERB_VARIETY_WORDS = new Set([
  'flat', 'curly', 'italian', 'french', 'greek', 'leaf', 'leaves', 'common',
])

const ASSUMED_STAPLE_IGNORE_WORDS = new Set([
  'tsp', 'teaspoon', 'teaspoons', 'tbsp', 'tablespoon', 'tablespoons',
  'cup', 'cups', 'pinch', 'pinches', 'dash', 'dashes', 'to', 'taste',
  'as', 'needed', 'and', 'or', 'plus', 'more', 'for', 'seasoning',
  'divided', 'optional',
])
const ASSUMED_SALT_WORDS = new Set(['salt', 'kosher', 'sea', 'table', 'fine', 'coarse'])
const ASSUMED_PEPPER_WORDS = new Set(['pepper', 'black', 'ground', 'fresh', 'freshly', 'cracked'])
const ASSUMED_SALT_PEPPER_WORDS = new Set([...ASSUMED_SALT_WORDS, ...ASSUMED_PEPPER_WORDS])

/**
 * Salt and black pepper are assumed pantry staples; avoid matching vegetable peppers.
 *
 * @param {string} line
 * @returns {boolean}
 */
export function lineIsAssumedPantryStaple(line) {
  const words = [...wordsForMatch(line)].filter((w) => !ASSUMED_STAPLE_IGNORE_WORDS.has(w))
  if (words.length === 0) return false
  const hasStaple = words.includes('salt') || words.includes('pepper')
  return hasStaple && words.every((w) => ASSUMED_SALT_PEPPER_WORDS.has(w))
}

/**
 * @param {Set<string>} wordSet
 * @returns {Set<string>}
 */
function effectiveSignificantWords(wordSet) {
  /** @type {Set<string>} */
  const out = new Set()
  for (const w of wordSet) {
    if (PREP_AND_QUALITY_MODIFIERS.has(w) || OPTIONAL_CUT_FORM_WORDS.has(w)) continue
    const aliased = aliasSpellingToken(w)
    const singular = singularForMatch(aliased)
    const chile = chileCanonicalToken(singular) || chileCanonicalToken(aliased)
    out.add(aliasSpellingToken(chile || singular))
  }
  for (const [head, optional] of Object.entries(CONDIMENT_OPTIONAL_FORM_WORDS)) {
    if (out.has(head)) {
      for (const w of optional) out.delete(w)
    }
  }
  for (const w of NAMED_FRESH_PEPPER_HEAD_WORDS) {
    if (out.has(w)) {
      for (const generic of GENERIC_PEPPER_POD_WORDS) out.delete(generic)
      break
    }
  }
  return out
}

/**
 * @param {Set<string>} headSide
 * @param {string[]} distinctExtra
 * @returns {boolean}
 */
function condimentOptionalFormAllowed(headSide, distinctExtra) {
  for (const [head, optional] of Object.entries(CONDIMENT_OPTIONAL_FORM_WORDS)) {
    if (headSide.has(head) && distinctExtra.every((w) => optional.has(w))) return true
  }
  return false
}

/**
 * True if pantryPhrase appears in lineLower as a whole word or phrase, not as letters inside
 * another word (e.g. "sage" must not match "sausage").
 *
 * @param {string} lineLower trimmed, lowercased, single-spaced grocery / ingredient line
 * @param {string} pantryPhrase trimmed, lowercased, single-spaced pantry item name
 * @returns {boolean}
 */
function lineContainsPantryPhrase(lineLower, pantryPhrase) {
  const lineFolded = foldAccentsForMatch(lineLower)
  const u = foldAccentsForMatch((pantryPhrase || '').trim().toLowerCase()).replace(/\s+/g, ' ')
  if (!u || !lineFolded) {
    return false
  }
  const parts = u.split(' ')
  const escaped = parts.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+')
  const re = new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i')
  return re.test(lineFolded)
}

/**
 * @param {Set<string>} sigR
 * @param {Set<string>} sigP
 * @returns {boolean}
 */
function chileFormsIncompatible(sigR, sigP) {
  const rPowder = sigR.has('powder') || sigR.has('powders')
  const pPowder = sigP.has('powder') || sigP.has('powders')
  const hasChile =
    [...sigR].some((w) => CHILE_PEPPER_WORDS.has(w)) ||
    [...sigP].some((w) => CHILE_PEPPER_WORDS.has(w))
  if (rPowder !== pPowder && (hasChile || rPowder || pPowder)) return true
  const rFlake = [...sigR].some((w) => CHILE_FLAKE_WORDS.has(w))
  const pFlake = [...sigP].some((w) => CHILE_FLAKE_WORDS.has(w))
  if (rFlake !== pFlake && (hasChile || rFlake || pFlake)) return true
  const rSpec = [...sigR].some((w) => CHILE_SPECIFICITY_WORDS.has(w))
  const pSpec = [...sigP].some((w) => CHILE_SPECIFICITY_WORDS.has(w))
  const rHasChile = [...sigR].some((w) => CHILE_PEPPER_WORDS.has(w))
  const pHasChile = [...sigP].some((w) => CHILE_PEPPER_WORDS.has(w))
  if (rSpec && !pSpec && pHasChile) return true
  if (pSpec && !rSpec && rHasChile) return true
  return false
}

/**
 * Core token overlap rules from backend `_word_sets_match_for_pantry`.
 *
 * @param {Set<string>} recipeWords
 * @param {Set<string>} pantryWords
 * @returns {boolean}
 */
function wordSetsMatchForPantry(recipeWords, pantryWords) {
  if (!recipeWords.size || !pantryWords.size) return false
  let overlap = false
  for (const w of recipeWords) {
    if (pantryWords.has(w)) {
      overlap = true
      break
    }
  }
  if (!overlap) return false
  if (recipeWords.size === 1 && pantryWords.size === 1) return true

  const sigR = effectiveSignificantWords(recipeWords)
  const sigP = effectiveSignificantWords(pantryWords)
  if (!sigR.size || !sigP.size) return overlap

  if (sigR.size === 1 && sigP.size === 1) {
    for (const w of sigR) {
      if (sigP.has(w)) return true
    }
    return false
  }

  if (chileFormsIncompatible(sigR, sigP)) return false

  if (sigR.size >= 2 && sigP.size >= 2) {
    let shared = false
    for (const w of sigR) {
      if (sigP.has(w)) {
        shared = true
        break
      }
    }
    if (!shared) return false
    const smaller = sigR.size <= sigP.size ? sigR : sigP
    const larger = smaller === sigR ? sigP : sigR
    for (const w of smaller) {
      if (larger.has(w)) continue
      const singular = singularForMatch(w)
      if (larger.has(singular)) continue
      let singularHit = false
      for (const lw of larger) {
        if (singularForMatch(lw) === w) {
          singularHit = true
          break
        }
      }
      if (singularHit) continue
      return false
    }
    return true
  }

  const larger = sigR.size >= sigP.size ? sigR : sigP
  const smaller = larger === sigR ? sigP : sigR
  for (const w of smaller) {
    if (!larger.has(w)) return false
  }
  /** @type {Set<string>} */
  const extra = new Set()
  for (const w of larger) {
    if (!smaller.has(w)) extra.add(w)
  }
  if (extra.size === 0) return true
  if ([...extra].every((w) => MEAT_CUT_WORDS.has(w))) return true
  const distinctExtra = [...extra].filter((w) => DISTINCT_PRODUCT_FORM_WORDS.has(w))
  if (distinctExtra.length > 0) {
    const herbOk =
      [...smaller].some((w) => HERB_HEAD_WORDS.has(w)) &&
      distinctExtra.every((w) => HERB_VARIETY_WORDS.has(w))
    if (!herbOk && !condimentOptionalFormAllowed(smaller, distinctExtra)) return false
  }
  return true
}

/**
 * @param {string} ingLower
 * @param {Set<string>} recipeWords
 * @param {string} pantryName
 * @returns {boolean}
 */
function pantryPhraseMatchAllowed(ingLower, recipeWords, pantryName) {
  if (!lineContainsPantryPhrase(ingLower, pantryName)) return false
  const sigR = effectiveSignificantWords(recipeWords)
  const sigP = effectiveSignificantWords(wordsForMatch(pantryName))
  if (chileFormsIncompatible(sigR, sigP)) return false
  if (sigR.size >= 2 && sigP.size > 0) {
    /** @type {Set<string>} */
    const extra = new Set()
    for (const w of sigR) {
      if (!sigP.has(w)) extra.add(w)
    }
    const distinctExtra = [...extra].filter((w) => DISTINCT_PRODUCT_FORM_WORDS.has(w))
    if (distinctExtra.length > 0) {
      const herbOk =
        [...sigP].some((w) => HERB_HEAD_WORDS.has(w)) &&
        distinctExtra.every((w) => HERB_VARIETY_WORDS.has(w))
      if (!herbOk && !condimentOptionalFormAllowed(sigP, distinctExtra)) return false
    }
  }
  return true
}

/**
 * True if a shopping-list / recipe ingredient line is covered by the user's pantry names.
 *
 * @param {string} line
 * @param {string[]} pantryNames normalized pantry item_name values (trim, lower, collapse spaces)
 * @returns {boolean}
 */
export function groceryLineCoveredByPantry(line, pantryNames) {
  if (!line?.trim()) {
    return false
  }
  if (lineIsAssumedPantryStaple(line)) {
    return true
  }
  if (!pantryNames?.length) {
    return false
  }
  const normalizedNames = pantryNames
    .map((n) => (n || '').trim().toLowerCase().replace(/\s+/g, ' '))
    .filter(Boolean)
  if (!normalizedNames.length) {
    return false
  }
  const ingLower = line.trim().toLowerCase().replace(/\s+/g, ' ')
  const recipeWords = wordsForMatch(line)
  for (const pantryItem of normalizedNames) {
    const pantryWords = wordsForMatch(pantryItem)
    if (wordSetsMatchForPantry(recipeWords, pantryWords)) {
      return true
    }
    if (pantryPhraseMatchAllowed(ingLower, recipeWords, pantryItem)) {
      return true
    }
  }
  return false
}
